import React, { useEffect, useState, useRef, Component } from 'react';
import { Tool, Opening, Obstacle, Soffit } from '../types/room';
import { motion, AnimatePresence } from 'framer-motion';
import { MousePointer2, Copy, Trash2, X, ChevronLeft, ChevronDown, RectangleHorizontal, DoorOpen, Layers, BoxIcon } from 'lucide-react';
interface ToolbarProps {
  activeTool: Tool;
  onToolChange: (tool: Tool) => void;
  onWallToolClick: () => void;
  onSoffitToolClick?: () => void;
  selectedObject?: Opening | Obstacle | Soffit | null;
  selectedObjectType?: 'opening' | 'obstacle' | 'soffit' | null;
  onUpdateObject?: (updates: any) => void;
  onDuplicateObject?: () => void;
  onDeleteObject?: () => void;
  onDeselectObject?: () => void;
  selectedTemplate?: string;
  onSelectTemplate?: (template: string) => void;
  selectedSoffitTemplate?: string;
  onSelectSoffitTemplate?: (template: string) => void;
}
interface ElementConfig {
  id: Tool;
  label: string;
  schematic: string;
  dimensions: {
    width?: number;
    height?: number;
    elevation?: number;
  };
}
const elements: ElementConfig[] = [{
  id: 'window',
  label: 'Window',
  schematic: '',
  dimensions: {
    width: 120,
    height: 150,
    elevation: 90
  }
}, {
  id: 'door',
  label: 'Door',
  schematic: '',
  dimensions: {
    width: 80,
    height: 210,
    elevation: 0
  }
}, {
  id: 'obstacle',
  label: 'Column',
  schematic: '',
  dimensions: {
    width: 40,
    height: 40,
    elevation: 0
  }
}, {
  id: 'soffit',
  label: 'Ceiling Drop',
  schematic: '',
  dimensions: {
    width: 100,
    height: 30,
    elevation: 240
  }
}];
const soffitTemplates = [{
  id: 'free' as const,
  name: 'Free Draw',
  description: 'Custom shape',
  path: 'M 15 30 L 30 15 L 50 20 L 65 35 L 55 50 L 25 45 Z'
}, {
  id: 'rectangle' as const,
  name: 'Rectangle',
  description: 'Rectangular drop',
  path: 'M 10 10 L 70 10 L 70 50 L 10 50 Z'
}, {
  id: 'l-shape' as const,
  name: 'L-Shape',
  description: 'L-shaped drop',
  path: 'M 10 10 L 50 10 L 50 35 L 70 35 L 70 50 L 10 50 Z'
}, {
  id: 'u-shape' as const,
  name: 'U-Shape',
  description: 'U-shaped drop',
  path: 'M 10 10 L 70 10 L 70 50 L 55 50 L 55 25 L 25 25 L 25 50 L 10 50 Z'
}, {
  id: 'galley' as const,
  name: 'Galley',
  description: 'Galley drop',
  path: 'M 10 20 L 70 20 M 10 40 L 70 40'
}];
export function Toolbar({
  activeTool,
  onToolChange,
  onWallToolClick,
  onSoffitToolClick,
  selectedObject,
  selectedObjectType,
  onUpdateObject,
  onDuplicateObject,
  onDeleteObject,
  onDeselectObject,
  selectedTemplate = 'free',
  onSelectTemplate,
  selectedSoffitTemplate = 'free',
  onSelectSoffitTemplate
}: ToolbarProps) {
  const [dimensions, setDimensions] = useState<Record<string, any>>({
    window: {
      width: 120,
      height: 150,
      elevation: 90
    },
    door: {
      width: 80,
      height: 210,
      elevation: 0
    },
    obstacle: {
      width: 40,
      height: 40,
      elevation: 0
    },
    soffit: {
      width: 100,
      height: 30,
      elevation: 240
    }
  });
  const [viewMode, setViewMode] = useState<'walls' | 'elements'>('walls');
  const [soffitDropdownOpen, setSoffitDropdownOpen] = useState(false);
  const soffitButtonRef = useRef<HTMLDivElement>(null);
  const soffitDropdownRef = useRef<HTMLDivElement>(null);
  const isEditMode = selectedObject !== null && selectedObject !== undefined;
  // Get object display name
  const getObjectDisplayName = () => {
    if (!selectedObject || !selectedObjectType) return '';
    if (selectedObjectType === 'opening') {
      const opening = selectedObject as Opening;
      return opening.type === 'window' ? 'Window' : 'Door';
    }
    if (selectedObjectType === 'obstacle') return 'Column';
    if (selectedObjectType === 'soffit') return 'Ceiling Drop';
    return 'Element';
  };
  // Get current object dimensions
  const getCurrentDimensions = () => {
    if (!selectedObject) return {
      width: 0,
      height: 0,
      elevation: 0
    };
    if (selectedObjectType === 'opening') {
      const opening = selectedObject as Opening;
      return {
        width: opening.width,
        height: opening.height,
        elevation: opening.elevation
      };
    }
    if (selectedObjectType === 'obstacle') {
      const obstacle = selectedObject as Obstacle;
      return {
        width: obstacle.width,
        height: obstacle.height || obstacle.depth,
        elevation: obstacle.elevation || 0
      };
    }
    if (selectedObjectType === 'soffit') {
      const soffit = selectedObject as Soffit;
      return {
        width: 100,
        height: soffit.drop,
        elevation: 240
      };
    }
    return {
      width: 0,
      height: 0,
      elevation: 0
    };
  };
  const currentDims = getCurrentDimensions();
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (soffitDropdownOpen && soffitButtonRef.current && soffitDropdownRef.current && !soffitButtonRef.current.contains(event.target as Node) && !soffitDropdownRef.current.contains(event.target as Node)) {
        setSoffitDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [soffitDropdownOpen]);
  return <div className="w-72 bg-[#2D2D2D] border-r border-[#4A4A4A] flex flex-col overflow-hidden" data-id="element-499">
      <AnimatePresence mode="wait" data-id="element-500">
        {!isEditMode ?
      // STATE A: LIBRARY MODE
      <motion.div key="library" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.2
      }} className="flex flex-col h-full" data-id="element-501">
            {/* Header with Toggle Slider */}
            <div className="p-4 border-b border-[#4A4A4A]" data-id="element-502">
              <div className="text-[#808080] text-xs font-medium tracking-wider mb-3" data-id="element-503">
                STRUCTURAL ELEMENTS
              </div>

              {/* Toggle Slider - CADVIX Style */}
              <div className="relative bg-[#1E1E1E] rounded-lg p-1 flex" data-id="element-504">
                <motion.div className="absolute top-1 bottom-1 bg-[#4FC3F7] rounded-md" initial={false} animate={{
              left: viewMode === 'walls' ? '4px' : '50%',
              right: viewMode === 'walls' ? '50%' : '4px'
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-505" />
                <button onClick={() => setViewMode('walls')} className={`relative z-10 flex-1 py-2 text-sm font-semibold rounded-md transition-colors ${viewMode === 'walls' ? 'text-white' : 'text-[#808080] hover:text-[#B0B0B0]'}`} data-id="element-506">
                  WALLS
                </button>
                <button onClick={() => setViewMode('elements')} className={`relative z-10 flex-1 py-2 text-sm font-semibold rounded-md transition-colors ${viewMode === 'elements' ? 'text-white' : 'text-[#808080] hover:text-[#B0B0B0]'}`} data-id="element-507">
                  ELEMENTS
                </button>
              </div>
            </div>

            {/* Content based on view mode */}
            <AnimatePresence mode="wait" data-id="element-508">
              {viewMode === 'walls' ?
          // WALLS VIEW
          <motion.div key="walls-view" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} className="flex-1 flex flex-col overflow-hidden" data-id="element-509">
                  {/* Wall Templates Section */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3" data-id="element-510">
                    <div className="text-[#808080] text-xs font-medium mb-3" data-id="element-511">
                      WALL TEMPLATES
                    </div>

                    {/* Free Draw */}
                    <motion.button onClick={() => {
                onToolChange('wall');
                onSelectTemplate?.('free');
              }} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className={`
                        w-full rounded-lg border-2 overflow-hidden transition-all
                        flex items-center gap-3 p-3
                        ${activeTool === 'wall' && selectedTemplate === 'free' ? 'border-[#4FC3F7] bg-[#4FC3F7]/10' : 'border-[#4A4A4A] bg-[#3A3A3A] hover:border-[#555555]'}
                      `} data-id="element-512">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#1E1E1E] rounded border border-[#4A4A4A] flex-shrink-0" data-id="element-513">
                        <svg width="32" height="32" viewBox="0 0 24 24" className={activeTool === 'wall' && selectedTemplate === 'free' ? 'stroke-[#4FC3F7]' : 'stroke-[#B0B0B0]'} fill="none" strokeWidth="2" data-id="element-514">
                          <path d="M3 12 L8 8 L12 12 L16 6 L21 10" strokeLinecap="round" strokeLinejoin="round" data-id="element-515" />
                        </svg>
                      </div>
                      <div className="flex-1 text-left" data-id="element-516">
                        <div className={`font-medium text-sm ${activeTool === 'wall' && selectedTemplate === 'free' ? 'text-[#4FC3F7]' : 'text-white'}`} data-id="element-517">
                          Free Draw
                        </div>
                        <div className="text-[#808080] text-[10px] mt-0.5" data-id="element-518">
                          Click to activate
                        </div>
                      </div>
                    </motion.button>

                    {/* Rectangle */}
                    <motion.button onClick={() => {
                onToolChange('wall');
                onSelectTemplate?.('rectangle');
              }} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className={`
                        w-full rounded-lg border-2 overflow-hidden transition-all
                        flex items-center gap-3 p-3
                        ${activeTool === 'wall' && selectedTemplate === 'rectangle' ? 'border-[#4FC3F7] bg-[#4FC3F7]/10' : 'border-[#4A4A4A] bg-[#3A3A3A] hover:border-[#555555]'}
                      `} data-id="element-519">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#1E1E1E] rounded border border-[#4A4A4A] flex-shrink-0" data-id="element-520">
                        <svg width="32" height="32" viewBox="0 0 24 24" className={activeTool === 'wall' && selectedTemplate === 'rectangle' ? 'stroke-[#4FC3F7]' : 'stroke-[#B0B0B0]'} fill="none" strokeWidth="2" data-id="element-521">
                          <rect x="5" y="7" width="14" height="10" rx="1" data-id="element-522" />
                        </svg>
                      </div>
                      <div className="flex-1 text-left" data-id="element-523">
                        <div className={`font-medium text-sm ${activeTool === 'wall' && selectedTemplate === 'rectangle' ? 'text-[#4FC3F7]' : 'text-white'}`} data-id="element-524">
                          Rectangle
                        </div>
                        <div className="text-[#808080] text-[10px] mt-0.5" data-id="element-525">
                          Click to activate
                        </div>
                      </div>
                    </motion.button>

                    {/* L-Shape */}
                    <motion.button onClick={() => {
                onToolChange('wall');
                onSelectTemplate?.('l-shape');
              }} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className={`
                        w-full rounded-lg border-2 overflow-hidden transition-all
                        flex items-center gap-3 p-3
                        ${activeTool === 'wall' && selectedTemplate === 'l-shape' ? 'border-[#4FC3F7] bg-[#4FC3F7]/10' : 'border-[#4A4A4A] bg-[#3A3A3A] hover:border-[#555555]'}
                      `} data-id="element-526">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#1E1E1E] rounded border border-[#4A4A4A] flex-shrink-0" data-id="element-527">
                        <svg width="32" height="32" viewBox="0 0 24 24" className={activeTool === 'wall' && selectedTemplate === 'l-shape' ? 'stroke-[#4FC3F7]' : 'stroke-[#B0B0B0]'} fill="none" strokeWidth="2" data-id="element-528">
                          <path d="M6 6 L18 6 L18 12 L12 12 L12 18 L6 18 Z" data-id="element-529" />
                        </svg>
                      </div>
                      <div className="flex-1 text-left" data-id="element-530">
                        <div className={`font-medium text-sm ${activeTool === 'wall' && selectedTemplate === 'l-shape' ? 'text-[#4FC3F7]' : 'text-white'}`} data-id="element-531">
                          L-Shape
                        </div>
                        <div className="text-[#808080] text-[10px] mt-0.5" data-id="element-532">
                          Click to activate
                        </div>
                      </div>
                    </motion.button>

                    {/* U-Shape */}
                    <motion.button onClick={() => {
                onToolChange('wall');
                onSelectTemplate?.('u-shape');
              }} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className={`
                        w-full rounded-lg border-2 overflow-hidden transition-all
                        flex items-center gap-3 p-3
                        ${activeTool === 'wall' && selectedTemplate === 'u-shape' ? 'border-[#4FC3F7] bg-[#4FC3F7]/10' : 'border-[#4A4A4A] bg-[#3A3A3A] hover:border-[#555555]'}
                      `} data-id="element-533">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#1E1E1E] rounded border border-[#4A4A4A] flex-shrink-0" data-id="element-534">
                        <svg width="32" height="32" viewBox="0 0 24 24" className={activeTool === 'wall' && selectedTemplate === 'u-shape' ? 'stroke-[#4FC3F7]' : 'stroke-[#B0B0B0]'} fill="none" strokeWidth="2" data-id="element-535">
                          <path d="M6 6 L6 18 L18 18 L18 6 M6 6 L10 6 M18 6 L14 6" data-id="element-536" />
                        </svg>
                      </div>
                      <div className="flex-1 text-left" data-id="element-537">
                        <div className={`font-medium text-sm ${activeTool === 'wall' && selectedTemplate === 'u-shape' ? 'text-[#4FC3F7]' : 'text-white'}`} data-id="element-538">
                          U-Shape
                        </div>
                        <div className="text-[#808080] text-[10px] mt-0.5" data-id="element-539">
                          Click to activate
                        </div>
                      </div>
                    </motion.button>

                    {/* Galley */}
                    <motion.button onClick={() => {
                onToolChange('wall');
                onSelectTemplate?.('galley');
              }} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className={`
                        w-full rounded-lg border-2 overflow-hidden transition-all
                        flex items-center gap-3 p-3
                        ${activeTool === 'wall' && selectedTemplate === 'galley' ? 'border-[#4FC3F7] bg-[#4FC3F7]/10' : 'border-[#4A4A4A] bg-[#3A3A3A] hover:border-[#555555]'}
                      `} data-id="element-540">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#1E1E1E] rounded border border-[#4A4A4A] flex-shrink-0" data-id="element-541">
                        <svg width="32" height="32" viewBox="0 0 24 24" className={activeTool === 'wall' && selectedTemplate === 'galley' ? 'stroke-[#4FC3F7]' : 'stroke-[#B0B0B0]'} fill="none" strokeWidth="2" data-id="element-542">
                          <path d="M6 8 L18 8 M6 16 L18 16 M6 8 L6 16 M18 8 L18 16" data-id="element-543" />
                        </svg>
                      </div>
                      <div className="flex-1 text-left" data-id="element-544">
                        <div className={`font-medium text-sm ${activeTool === 'wall' && selectedTemplate === 'galley' ? 'text-[#4FC3F7]' : 'text-white'}`} data-id="element-545">
                          Galley
                        </div>
                        <div className="text-[#808080] text-[10px] mt-0.5" data-id="element-546">
                          Click to activate
                        </div>
                      </div>
                    </motion.button>
                  </div>
                </motion.div> :
          // ELEMENTS VIEW
          <motion.div key="elements-view" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} transition={{
            duration: 0.2
          }} className="flex-1 overflow-y-auto p-4 space-y-3" data-id="element-547">
                  {elements.map(element => {
              const isActive = activeTool === element.id;
              const isSoffit = element.id === 'soffit';
              // Get the appropriate icon component
              const IconComponent = element.id === 'window' ? RectangleHorizontal : element.id === 'door' ? DoorOpen : element.id === 'obstacle' ? BoxIcon : element.id === 'soffit' ? Layers : BoxIcon;
              return <div key={element.id} className="relative" ref={isSoffit ? soffitButtonRef : null} data-id="element-548">
                        <motion.button onClick={() => {
                  if (isSoffit) {
                    setSoffitDropdownOpen(!soffitDropdownOpen);
                  } else {
                    onToolChange(element.id);
                  }
                }} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className={`
                            w-full rounded-lg border-2 overflow-hidden transition-all
                            flex items-center gap-3 p-3
                            ${isActive ? 'border-[#4FC3F7] bg-[#4FC3F7]/10' : 'border-[#4A4A4A] bg-[#3A3A3A] hover:border-[#555555]'}
                          `} data-id="element-549">
                          <div className="w-14 h-14 flex items-center justify-center bg-[#1E1E1E] rounded border border-[#4A4A4A] flex-shrink-0" data-id="element-550">
                            <IconComponent size={32} className={isActive ? 'text-[#4FC3F7]' : 'text-[#B0B0B0]'} strokeWidth={1.5} data-id="element-551" />
                          </div>

                          <div className="flex-1 text-left" data-id="element-552">
                            <div className={`font-medium text-sm ${isActive ? 'text-[#4FC3F7]' : 'text-white'}`} data-id="element-553">
                              {element.label}
                            </div>
                            <div className="text-[#808080] text-[10px] mt-0.5" data-id="element-554">
                              Click to activate
                            </div>
                          </div>

                          {isSoffit && <ChevronDown size={16} className={`transition-transform ${soffitDropdownOpen ? 'rotate-180' : ''} ${isActive ? 'text-[#4FC3F7]' : 'text-[#B0B0B0]'}`} data-id="element-555" />}
                        </motion.button>

                        {/* Soffit Dropdown */}
                        {isSoffit && <AnimatePresence data-id="element-556">
                            {soffitDropdownOpen && <motion.div ref={soffitDropdownRef} initial={{
                    opacity: 0,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -10
                  }} transition={{
                    duration: 0.15
                  }} className="absolute top-full left-0 right-0 mt-2 bg-[#1E1E1E] border-2 border-[#4A4A4A] rounded-lg overflow-hidden z-50 shadow-xl" data-id="element-557">
                                <div className="p-2 space-y-1" data-id="element-558">
                                  {soffitTemplates.map(template => {
                        const isSelected = activeTool === 'soffit' && selectedSoffitTemplate === template.id;
                        return <motion.button key={template.id} onClick={() => {
                          onToolChange('soffit');
                          onSelectSoffitTemplate?.(template.id);
                          setSoffitDropdownOpen(false);
                        }} whileHover={{
                          scale: 1.02
                        }} whileTap={{
                          scale: 0.98
                        }} className={`
                                          w-full p-3 rounded-lg transition-all text-left
                                          flex items-center gap-3
                                          ${isSelected ? 'bg-[#4FC3F7]/20 border-2 border-[#4FC3F7]' : 'bg-[#3A3A3A] border-2 border-transparent hover:border-[#555555]'}
                                        `} data-id="element-559">
                                        {/* Template Preview */}
                                        <div className="w-12 h-12 flex items-center justify-center bg-[#0a0a0a] rounded border border-[#4A4A4A] flex-shrink-0" data-id="element-560">
                                          <svg width="40" height="40" viewBox="0 0 80 60" data-id="element-561">
                                            <path d={template.path} fill="none" stroke={isSelected ? '#4FC3F7' : '#888'} strokeWidth="2" strokeDasharray="3,3" data-id="element-562" />
                                          </svg>
                                        </div>

                                        {/* Template Info */}
                                        <div className="flex-1 min-w-0" data-id="element-563">
                                          <div className={`font-medium text-sm ${isSelected ? 'text-[#4FC3F7]' : 'text-white'}`} data-id="element-564">
                                            {template.name}
                                          </div>
                                          <div className="text-[#808080] text-[10px] mt-0.5" data-id="element-565">
                                            {template.description}
                                          </div>
                                        </div>

                                        {/* Selection Indicator */}
                                        {isSelected && <div className="w-2 h-2 rounded-full bg-[#4FC3F7] flex-shrink-0" data-id="element-566" />}
                                      </motion.button>;
                      })}
                                </div>
                              </motion.div>}
                          </AnimatePresence>}
                      </div>;
            })}
                </motion.div>}
            </AnimatePresence>

            {/* Footer */}
            <div className="p-4 border-t border-[#4A4A4A]" data-id="element-567">
              <div className="text-[#808080] text-[10px] leading-relaxed" data-id="element-568">
                <div className="text-[#4FC3F7] mb-1 font-medium" data-id="element-569">
                  KEYBOARD SHORTCUTS:
                </div>
                V = Select • W = Walls • G = Guides
                <br data-id="element-570" />
                ESC = Cancel • DEL = Delete
              </div>
            </div>
          </motion.div> :
      // STATE B: EDIT MODE
      <motion.div key="edit" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 20
      }} transition={{
        duration: 0.2
      }} className="flex flex-col h-full" data-id="element-571">
            {/* Header with Back Button */}
            <div className="p-4 border-b border-[#4A4A4A] flex items-center justify-between" data-id="element-572">
              <div data-id="element-573">
                <div className="text-[#4FC3F7] text-xs font-medium tracking-wider mb-1" data-id="element-574">
                  EDITING
                </div>
                <div className="text-white text-lg font-mono font-bold" data-id="element-575">
                  {getObjectDisplayName()}
                </div>
              </div>
              <motion.button whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} onClick={onDeselectObject} className="w-8 h-8 flex items-center justify-center text-[#808080] hover:text-white hover:bg-[#3A3A3A] rounded transition-colors" title="Close (ESC)" data-id="element-576">
                <X size={18} data-id="element-577" />
              </motion.button>
            </div>

            {/* Properties Section */}
            <div className="flex-1 overflow-y-auto p-4" data-id="element-578">
              <div className="space-y-4" data-id="element-579">
                <div className="text-[#808080] text-xs font-medium tracking-wider mb-3" data-id="element-580">
                  DIMENSIONS
                </div>

                {/* Width */}
                <div data-id="element-581">
                  <label className="text-[#808080] text-sm font-medium block mb-2" data-id="element-582">
                    Width (cm)
                  </label>
                  <input type="number" value={currentDims.width} onChange={e => onUpdateObject?.({
                width: Number(e.target.value)
              })} className="w-full bg-[#1E1E1E] border-2 border-[#4A4A4A] rounded-lg px-4 py-3 text-white text-lg font-mono font-bold focus:border-[#4FC3F7] focus:outline-none transition-colors" data-id="element-583" />
                </div>

                {/* Height */}
                <div data-id="element-584">
                  <label className="text-[#808080] text-sm font-medium block mb-2" data-id="element-585">
                    Height (cm)
                  </label>
                  <input type="number" value={currentDims.height} onChange={e => {
                if (selectedObjectType === 'obstacle') {
                  onUpdateObject?.({
                    depth: Number(e.target.value)
                  });
                } else if (selectedObjectType === 'soffit') {
                  onUpdateObject?.({
                    drop: Number(e.target.value)
                  });
                } else {
                  onUpdateObject?.({
                    height: Number(e.target.value)
                  });
                }
              }} className="w-full bg-[#1E1E1E] border-2 border-[#4A4A4A] rounded-lg px-4 py-3 text-white text-lg font-mono font-bold focus:border-[#4FC3F7] focus:outline-none transition-colors" data-id="element-586" />
                </div>

                {/* Elevation */}
                {selectedObjectType !== 'soffit' && <div data-id="element-587">
                    <label className="text-[#808080] text-sm font-medium block mb-2" data-id="element-588">
                      Elevation (cm)
                    </label>
                    <input type="number" value={currentDims.elevation} onChange={e => onUpdateObject?.({
                elevation: Number(e.target.value)
              })} className="w-full bg-[#1E1E1E] border-2 border-[#4A4A4A] rounded-lg px-4 py-3 text-white text-lg font-mono font-bold focus:border-[#4FC3F7] focus:outline-none transition-colors" data-id="element-589" />
                  </div>}
              </div>
            </div>

            {/* Action Buttons Footer */}
            <div className="p-4 border-t border-[#4A4A4A] space-y-2" data-id="element-590">
              <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={onDuplicateObject} className="w-full h-12 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-white rounded-lg flex items-center justify-center gap-2 font-mono text-sm font-semibold transition-colors" data-id="element-591">
                <Copy size={16} data-id="element-592" />
                Duplicate
              </motion.button>

              <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={onDeleteObject} className="w-full h-12 bg-[#ef4444]/10 hover:bg-[#ef4444]/20 text-[#ef4444] border-2 border-[#ef4444]/30 hover:border-[#ef4444] rounded-lg flex items-center justify-center gap-2 font-mono text-sm font-semibold transition-all" data-id="element-593">
                <Trash2 size={16} data-id="element-594" />
                Delete
              </motion.button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}